'use client';
import { useEffect, useRef, useState } from 'react';
import { cn } from '../cn';
import type { WidgetGroup } from './document-list-shell-types';

interface CustomizeProps {
  widgets: WidgetGroup[];
  hidden: Set<string>;
  onChange: (next: Set<string>) => void;
  triggerLabel?: string;
}

/**
 * Customize widgets popover — buton trigger + popover cu toggle per widget.
 * Generalizare a CustomizePopover-ului din /facturi, fără filter docType
 * (consumer filtrează widgets înainte de a-i pasa). Outside-click + Escape close.
 */
export function DocumentListShellCustomize({
  widgets,
  hidden,
  onChange,
  triggerLabel = 'Personalizează afișarea',
}: CustomizeProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = (id: string) => {
    const next = new Set(hidden);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(next);
  };
  const reset = () => onChange(new Set());
  const hideAll = () => {
    const all = new Set<string>();
    widgets.forEach((g) => g.items.forEach((it) => all.add(it.id)));
    onChange(all);
  };

  const totalCount = widgets.reduce((s, g) => s + g.items.length, 0);
  const visibleCount = totalCount - widgets.reduce((s, g) => s + g.items.filter((it) => hidden.has(it.id)).length, 0);
  const hiddenCount = totalCount - visibleCount;

  return (
    <div ref={ref} className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={triggerLabel}
        className="inline-grid h-8 w-8 place-items-center rounded-md border border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
        title={triggerLabel}
      >
        <SlidersIcon />
      </button>
      {open && (
        <div
          role="dialog"
          aria-label={triggerLabel}
          className="absolute right-0 top-[calc(100%+6px)] z-40 w-[300px] overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-2xl"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b border-neutral-100 px-3 py-2">
            <div>
              <div className="text-xs font-bold tracking-tight text-neutral-900">{triggerLabel}</div>
              <div className="mt-0.5 text-[0.6875rem] text-neutral-500">{visibleCount} din {totalCount} afișate</div>
            </div>
          </div>
          <div className="max-h-[360px] overflow-y-auto">
            {widgets.map((group) => (
              <div key={group.title} className="px-3 pb-1.5 pt-2">
                <div className="mb-1.5 text-[0.625rem] font-bold uppercase tracking-wider text-neutral-500">{group.title}</div>
                {group.items.map((it) => {
                  const visible = !hidden.has(it.id);
                  return (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => toggle(it.id)}
                      className="-mx-2 flex w-[calc(100%+1rem)] items-start gap-2.5 rounded px-2 py-1.5 text-left transition-colors hover:bg-neutral-50"
                    >
                      <span
                        className={cn(
                          'mt-0.5 flex h-4 w-7 flex-shrink-0 items-center rounded-full p-0.5 transition-colors',
                          visible ? 'justify-end bg-primary-500' : 'justify-start bg-neutral-300',
                        )}
                      >
                        <span className="h-3 w-3 rounded-full bg-white shadow" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className={cn('block text-xs font-medium leading-tight', visible ? 'text-neutral-900' : 'text-neutral-500')}>{it.label}</span>
                        {it.note && <span className="mt-0.5 block text-[0.6563rem] leading-tight text-neutral-500">{it.note}</span>}
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
          <div className="flex gap-1.5 border-t border-neutral-100 bg-neutral-50 px-3 py-2">
            <button
              type="button"
              onClick={hideAll}
              disabled={visibleCount === 0}
              className={cn(
                'flex-1 rounded border border-neutral-200 bg-white px-2 py-1.5 text-[0.7188rem] font-semibold text-neutral-700 transition-colors',
                visibleCount === 0 ? 'cursor-not-allowed opacity-40' : 'hover:bg-neutral-50',
              )}
            >Ascunde toate</button>
            <button
              type="button"
              onClick={reset}
              disabled={hiddenCount === 0}
              className={cn(
                'flex-1 rounded border border-neutral-200 bg-white px-2 py-1.5 text-[0.7188rem] font-semibold text-neutral-700 transition-colors',
                hiddenCount === 0 ? 'cursor-not-allowed opacity-40' : 'hover:bg-neutral-50',
              )}
            >Afișează toate{hiddenCount > 0 ? ` (${hiddenCount})` : ''}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function SlidersIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <line x1="4" y1="21" x2="4" y2="14" />
      <line x1="4" y1="10" x2="4" y2="3" />
      <line x1="12" y1="21" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12" y2="3" />
      <line x1="20" y1="21" x2="20" y2="16" />
      <line x1="20" y1="12" x2="20" y2="3" />
      <line x1="1" y1="14" x2="7" y2="14" />
      <line x1="9" y1="8" x2="15" y2="8" />
      <line x1="17" y1="16" x2="23" y2="16" />
    </svg>
  );
}
